import "./styles.css";
import { lists, words } from "./data";
import { icon } from "./icons";
import { msg, type MessageKey } from "./i18n";
import { displayTerm, displayTranslation, hasEditorialExample as isEditorialExample } from "./lexicon";
import { findNextReview, selectDueWords } from "./review";
import { exportState, importState, loadState, saveState, schedule, todayKey } from "./storage";
import type { AppState, Locale, ReviewRating, Route, StudyLanguage, Theme, Word } from "./types";

const appRoot = document.querySelector<HTMLDivElement>("#app");
if (!appRoot) throw new Error("Missing app root");
const app = appRoot;

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
}

type QuestionKind = "definition" | "meaning" | "example" | "spelling" | "synonym" | "antonym";
interface QuizOption { id: string; label: string }
interface QuizQuestion {
  kind: QuestionKind;
  prompt: string;
  stem: string;
  options: QuizOption[];
  correctId: string;
}
interface Session {
  words: Word[];
  index: number;
  correct: number;
  answered: boolean;
  wasCorrect: boolean;
  selectedId?: string;
  question?: QuizQuestion;
}

let state: AppState = loadState();
validateListSelection(state);
let route: Route = routeFromHash();
let listDetailId: string | null = listIdFromHash();
let wordDetailId: string | null = wordIdFromHash();
let wordReturnHash = "#library";
let session: Session | null = null;
let query = "";
let libraryFilter: "all" | "starred" = "all";
let selectedList = "all";
let discoveryIndex = 0;
let animateNextRender = true;
let deferredInstall: BeforeInstallPromptEvent | null = null;
let searchRenderTimer: number | undefined;

const t = (key: MessageKey): string => msg(state.locale, key);
const c = (fr: string, en: string): string => state.locale === "fr" ? fr : en;
const local = <T extends { fr: string; en: string }>(value: T): string => value[state.locale];
const localList = (value?: { fr: string[]; en: string[] }): string[] => value?.[state.locale] ?? [];
const wordTerm = (word: Word): string => displayTerm(word, state.locale);
const wordTranslation = (word: Word): string | undefined => displayTranslation(word, state.locale);
const hasEditorialExample = (word: Word): boolean => isEditorialExample(word, state.locale);
const wordInList = (word: Word, listId: string): boolean => (word.listIds ?? [word.listId]).includes(listId);
const enabledWords = (): Word[] => words.filter((word) => {
  const enabled = (word.listIds ?? [word.listId]).some((id) => state.enabledLists.includes(id));
  const languageMatches = state.studyLanguage === "all" || word.language === state.studyLanguage || (!word.language && Boolean(word.termEn) && (state.studyLanguage === "fr" || state.studyLanguage === "en"));
  return enabled && languageMatches;
});
const escapeHtml = (value: string): string => value.replace(/[&<>'"]/g, (char) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "'": "&#39;", '"': "&quot;" })[char] ?? char);
const slug = (value: string): string => value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "word";

function validateListSelection(value: AppState): void {
  value.enabledLists = value.enabledLists.filter((id) => lists.some((list) => list.id === id));
  if (!value.enabledLists.length) value.enabledLists = lists.filter((list) => list.defaultEnabled).map((list) => list.id);
}

function routeFromHash(): Route {
  const raw = location.hash.replace("#", "");
  if (raw.startsWith("list/")) return "lists";
  if (raw.startsWith("word/")) return "word";
  const value = raw as Route;
  return ["home", "library", "lists", "practice", "settings"].includes(value) ? value : "home";
}

function listIdFromHash(): string | null {
  const raw = location.hash.replace("#", "");
  if (!raw.startsWith("list/")) return null;
  const id = decodeURIComponent(raw.slice(5));
  return lists.some((list) => list.id === id) ? id : null;
}

function wordIdFromHash(): string | null {
  const raw = location.hash.replace("#", "");
  if (!raw.startsWith("word/")) return null;
  const id = decodeURIComponent(raw.slice(5));
  return words.some((word) => word.id === id) ? id : null;
}

function persist(): void {
  saveState(state);
  applyTheme();
}

function applyTheme(): void {
  document.documentElement.dataset.theme = state.theme;
  document.documentElement.lang = state.locale;
  const dark = state.theme === "dark" || (state.theme === "system" && matchMedia("(prefers-color-scheme: dark)").matches);
  document.querySelector('meta[name="theme-color"]')?.setAttribute("content", dark ? "#181714" : "#f4f0e6");
}

function showToast(message: string): void {
  document.querySelector(".app-toast")?.remove();
  const toast = document.createElement("div");
  toast.className = "app-toast";
  toast.setAttribute("role", "status");
  toast.setAttribute("aria-live", "polite");
  toast.textContent = message;
  document.body.append(toast);
  setTimeout(() => toast.remove(), 2600);
}

function navItem(name: Route, label: string, iconName: "home" | "library" | "lists" | "practice" | "settings"): string {
  return `<a class="nav-item ${route === name ? "active" : ""}" href="#${name}">${icon(iconName)}<span>${label}</span></a>`;
}

function render(): void {
  const content = route === "home" ? renderDiscover() : route === "library" ? renderLibrary() : route === "lists" ? (listDetailId ? renderListDetail(listDetailId) : renderLists()) : route === "practice" ? renderReview() : route === "word" ? renderWordPage(wordDetailId) : renderSettings();
  app.classList.toggle("skip-reveal", !animateNextRender);
  const existingMain = app.querySelector<HTMLElement>(".main-content");
  if (existingMain) {
    existingMain.innerHTML = content;
    app.querySelectorAll<HTMLAnchorElement>(".nav-item").forEach((item) => item.classList.toggle("active", item.getAttribute("href") === `#${route}`));
    animateNextRender = false;
    return;
  }
  app.innerHTML = `<div class="app-frame simple-frame">
    <aside class="side-rail simple-rail">
      <a class="brand" href="#home" aria-label="Ars Linguae"><span class="brand-mark">A</span><span>Ars Linguae</span></a>
      <nav>${navItem("home", c("Découvrir", "Discover"), "home")}${navItem("library", t("library"), "library")}${navItem("lists", c("Listes", "Lists"), "lists")}${navItem("practice", c("Réviser", "Review"), "practice")}</nav>
      <div class="rail-bottom">${navItem("settings", t("settings"), "settings")}<p>Ars longa, vita brevis.</p></div>
    </aside>
    <main class="main-content simple-main">${content}</main>
    <nav class="bottom-nav simple-bottom">${navItem("home", c("Découvrir", "Discover"), "home")}${navItem("library", c("Biblio.", "Library"), "library")}${navItem("lists", c("Listes", "Lists"), "lists")}${navItem("practice", c("Réviser", "Review"), "practice")}${navItem("settings", t("settings"), "settings")}</nav>
  </div>`;
  animateNextRender = false;
}

function pageHeader(kicker: string, title: string, action = ""): string {
  return `<header class="simple-header reveal"><div><p class="eyebrow">${kicker}</p><h1>${title}</h1></div>${action}</header>`;
}

function renderDiscover(): string {
  const active = enabledWords();
  const unseen = active.filter((word) => !state.seenWords.includes(word.id));
  const pool = unseen.length ? unseen : active;
  const word = pool[discoveryIndex % pool.length] ?? words[0];
  const list = lists.find((item) => item.id === word.listId)!;
  const saved = state.starred.includes(word.id);
  const translation = wordTranslation(word);
  return `<div class="simple-page discover-page">
    ${pageHeader(c("Un mot à la fois", "One word at a time"), c("Découvrir", "Discover"), `<a class="header-settings" href="#settings" aria-label="${t("settings")}">${icon("settings")}</a>`)}
    <article class="discovery-card reveal delay-1" style="--word-accent:${list.accent}">
      <div class="discovery-meta"><span>${local(list.title)}</span><span>${local(word.partOfSpeech)}${word.pronunciation ? ` · ${word.pronunciation}` : ""}</span></div>
      <h2>${wordTerm(word)}</h2>
      <div class="discovery-body">
        <p class="discovery-definition">${local(word.definition)}</p>
        ${hasEditorialExample(word) ? `<blockquote>${local(word.example)}</blockquote>` : ""}
        ${(translation || localList(word.synonyms).length || local(word.etymology)) ? `<div class="word-relations">
          ${translation ? `<div><span>${c("Traduction", "Translation")}</span><p>${translation}</p></div>` : localList(word.synonyms).length ? `<div><span>${c("Proches", "Related")}</span><p>${localList(word.synonyms).join(" · ")}</p></div>` : ""}
          ${local(word.etymology) ? `<div><span>${t("etymology")}</span><p>${local(word.etymology)}</p></div>` : ""}
        </div>` : ""}
      </div>
      <div class="discovery-actions">
        <button class="save-word ${saved ? "saved" : ""}" data-star="${word.id}">${icon("star", saved)}<span>${saved ? c("À retenir", "Saved to learn") : c("Retenir", "Remember")}</span></button>
        <a class="word-page-link" href="#word/${encodeURIComponent(word.id)}">${c("Lire la fiche", "Open word page")} ${icon("arrow")}</a>
        <button class="next-word" data-next-discovery><span>${c("Un autre mot", "Another word")}</span>${icon("arrow")}</button>
      </div>
    </article>
    <section class="simple-section reveal delay-2">
      <div class="section-title"><div><p class="eyebrow">${c("Explorer", "Explore")}</p><h2>${c("Parcourir les collections", "Browse collections")}</h2></div></div>
      <div class="simple-collections">${lists.filter((item) => state.enabledLists.includes(item.id)).slice(0, 4).map((item) => {
        const count = words.filter((entry) => wordInList(entry, item.id)).length;
        return `<button data-open-list="${item.id}" style="--word-accent:${item.accent}"><i></i><div><strong>${local(item.title)}</strong><span>${local(item.subtitle)}</span></div><small>${count} ${t("words")}</small>${icon("arrow")}</button>`;
      }).join("")}</div>
      <a class="manage-lists-link" href="#lists">${c(`Gérer les listes · ${lists.length - state.enabledLists.length} autres disponibles`, `Manage lists · ${lists.length - state.enabledLists.length} more available`)} ${icon("arrow")}</a>
    </section>
  </div>`;
}

function visibleWords(): Word[] {
  const normalized = query.trim().toLocaleLowerCase(state.locale);
  return words
    .filter((word) => selectedList === "all" || wordInList(word, selectedList))
    .filter((word) => libraryFilter === "all" || state.starred.includes(word.id))
    .filter((word) => !normalized || `${wordTerm(word)} ${word.translation ?? ""} ${(word.aliases ?? []).join(" ")} ${local(word.definition)} ${localList(word.synonyms).join(" ")}`.toLocaleLowerCase(state.locale).includes(normalized))
    .sort((a, b) => wordTerm(a).localeCompare(wordTerm(b), state.locale));
}

function renderLibrary(): string {
  const filtered = visibleWords();
  const groupIds = selectedList === "all"
    ? [...lists.filter((list) => state.enabledLists.includes(list.id)), ...lists.filter((list) => !state.enabledLists.includes(list.id))].map((list) => list.id)
    : [selectedList];
  const groupedIds = new Set<string>();
  const groups = groupIds.map((id) => {
    const list = lists.find((item) => item.id === id);
    const members = filtered.filter((word) => wordInList(word, id) && !groupedIds.has(word.id));
    members.forEach((word) => groupedIds.add(word.id));
    return list && members.length ? { list, members } : null;
  }).filter((group): group is { list: (typeof lists)[number]; members: Word[] } => Boolean(group));
  const remaining = filtered.filter((word) => !groupedIds.has(word.id));
  return `<div class="simple-page library-page">
    ${pageHeader(c("Le catalogue", "The catalogue"), t("library"), `<span class="header-count">${filtered.length}</span>`)}
    <section class="simple-tools reveal delay-1">
      <div class="search-field">${icon("search")}<input id="word-search" type="search" value="${escapeHtml(query)}" placeholder="${t("search")}" aria-label="${t("search")}" autocomplete="off"></div>
      <div class="library-controls">
        <div class="simple-tabs"><button class="${libraryFilter === "all" ? "active" : ""}" data-filter="all">${t("all")}</button><button class="${libraryFilter === "starred" ? "active" : ""}" data-filter="starred">${t("starred")} <span>${state.starred.length}</span></button></div>
        <select id="list-select" aria-label="${c("Collection", "Collection")}"><option value="all">${c("Tout le catalogue", "Entire catalogue")}</option>${lists.map((list) => `<option value="${list.id}" ${selectedList === list.id ? "selected" : ""}>${local(list.title)}</option>`).join("")}</select>
      </div>
    </section>
    <div class="library-list-groups reveal delay-2">${groups.map(({ list, members }) => `<section class="library-list-group" style="--word-accent:${list.accent}">
      <a href="#list/${encodeURIComponent(list.id)}"><div><span>${list.language === "fr" ? "FR" : list.language === "en" ? "EN" : list.language === "la" ? "LA" : "FR · EN"}</span><h2>${local(list.title)}</h2></div><small>${members.length} ${t("words")}</small>${icon("arrow")}</a>
      <div class="simple-word-list">${members.map(renderWordRow).join("")}</div>
    </section>`).join("")}${remaining.length ? `<section class="library-list-group"><div class="orphan-list-title"><h2>${t("starred")}</h2><small>${remaining.length}</small></div><div class="simple-word-list">${remaining.map(renderWordRow).join("")}</div></section>` : ""}${!filtered.length ? `<div class="empty-state"><p>${t("empty")}</p></div>` : ""}</div>
  </div>`;
}

function renderWordRow(word: Word): string {
  const saved = state.starred.includes(word.id);
  return `<article class="simple-word-row">
    <a class="word-row-link" href="#word/${encodeURIComponent(word.id)}"><span>${local(word.partOfSpeech)}</span><h2>${wordTerm(word)}</h2><p>${local(word.definition)}</p></a>
    <button class="star-button ${saved ? "starred" : ""}" data-star="${word.id}" aria-label="${saved ? t("removeStar") : t("addStar")}">${icon("star", saved)}</button>
  </article>`;
}

function renderWordPage(id: string | null): string {
  const word = words.find((item) => item.id === id);
  if (!word) return `<div class="simple-page word-page"><a class="back-to-lists" href="#library">← ${c("Mes mots", "My words")}</a><p>${t("empty")}</p></div>`;
  const saved = state.starred.includes(word.id);
  const translation = wordTranslation(word);
  const related = localList(word.relatedConcepts);
  const collocations = localList(word.collocations);
  const synonyms = localList(word.synonyms);
  const antonyms = localList(word.antonyms);
  const listTitles = (word.listIds ?? [word.listId])
    .map((id) => lists.find((list) => list.id === id))
    .filter((list): list is (typeof lists)[number] => Boolean(list))
    .map((list) => local(list.title));
  const usageNotes = word.usageNote ? [word.usageNote] : undefined;
  const sectionLinks = [
    ["definition", t("definition"), true],
    ["nuance", c("Nuance", "Nuance"), Boolean(word.nuancedMeaning?.length)],
    ["etymology", t("etymology"), Boolean(local(word.etymology))],
    ["usage", c("Usage", "Usage"), Boolean(usageNotes)],
    ["example", t("example"), hasEditorialExample(word)],
    ["relations", c("Relations", "Relations"), Boolean(synonyms.length || antonyms.length || related.length || collocations.length)],
  ].filter((item) => item[2]) as [string, string, boolean][];

  return `<article class="simple-page word-page" style="--word-accent:${lists.find((item) => item.id === word.listId)?.accent ?? "var(--accent)"}">
    <a class="back-to-lists" href="${wordReturnHash}">← ${c("Retour", "Back")}</a>
    <header class="word-hero">
      <p class="eyebrow">${local(word.partOfSpeech)}${word.pronunciation ? ` · ${word.pronunciation}` : ""}</p>
      <h1>${wordTerm(word)}</h1>
      ${translation ? `<p class="word-translation">${translation}</p>` : ""}
      <div class="word-hero-actions">
        <button class="word-action primary-action ${saved ? "saved" : ""}" data-star="${word.id}">${icon("star", saved)}<span>${saved ? c("À retenir", "Saved") : c("Retenir", "Remember")}</span></button>
        <button class="word-action" data-share-word="${word.id}">${icon("share")}<span>${c("Partager", "Share")}</span></button>
      </div>
    </header>
    ${sectionLinks.length > 2 ? `<nav class="word-section-nav" aria-label="${c("Sections de la fiche", "Word page sections")}">${sectionLinks.map(([id, label]) => `<button data-scroll-section="word-section-${id}">${label}</button>`).join("")}</nav>` : ""}
    <section class="word-section word-definition-section" id="word-section-definition">
      ${word.literalMeaning ? `<div class="word-kv"><span>${c("Sens littéral", "Literal meaning")}</span><p>${local(word.literalMeaning)}</p></div>` : ""}
      <h2>${t("definition")}</h2>
      <p>${local(word.definition)}</p>
    </section>
    ${renderTextSection(c("Nuance", "Nuanced Meaning"), word.nuancedMeaning, "word-section-nuance")}
    ${renderTextSection(t("etymology"), local(word.etymology) ? [word.etymology] : undefined, "word-section-etymology")}
    ${renderTextSection(c("Note d’usage", "Usage Note"), usageNotes, "word-section-usage")}
    ${hasEditorialExample(word) ? `<section class="word-section" id="word-section-example">
      <h2>${t("example")}</h2>
      <blockquote>${local(word.example)}</blockquote>
      ${(word.extraExamples ?? []).map((example) => `<article class="extra-example"><h3>${local(example.title)}</h3><p>${local(example.text)}</p></article>`).join("")}
    </section>` : ""}
    ${(synonyms.length || antonyms.length || related.length || collocations.length) ? `<section class="word-section word-list-section" id="word-section-relations">
      ${synonyms.length ? `<div><h2>${c("Synonymes", "Synonyms")}</h2><p>${synonyms.join(", ")}</p></div>` : ""}
      ${antonyms.length ? `<div><h2>${c("Antonymes", "Antonyms")}</h2><p>${antonyms.join(", ")}</p></div>` : ""}
      ${collocations.length ? `<div><h2>${c("Collocations", "Collocations")}</h2><p>${collocations.join(", ")}</p></div>` : ""}
      ${related.length ? `<div><h2>${c("Notions liées", "Related Concepts")}</h2><p>${related.join(", ")}</p></div>` : ""}
    </section>` : ""}
    ${word.distinctions?.length ? `<section class="word-section"><h2>${c("Distinguer", "Distinctions")}</h2>${word.distinctions.map((item) => `<article class="word-note"><h3>${local(item.title)}</h3><p>${local(item.body)}</p></article>`).join("")}</section>` : ""}
    ${renderTextSection(c("Erreur fréquente", "Common Mistake"), word.commonMistake ? [word.commonMistake] : undefined)}
    ${renderTextSection(c("Note historique", "Historical Note"), word.historicalNote ? [word.historicalNote] : undefined)}
    ${renderTextSection(c("Mémoire", "Memory Aid"), word.memoryAid ? [word.memoryAid] : undefined)}
    ${listTitles.length ? `<footer class="word-page-lists"><span>${c("Listes", "Lists")}</span><p>${listTitles.join(" · ")}</p></footer>` : ""}
  </article>`;
}

function renderTextSection(title: string, items?: { fr: string; en: string }[], id?: string): string {
  const body = items?.map((item) => local(item)).filter(Boolean);
  if (!body?.length) return "";
  return `<section class="word-section"${id ? ` id="${id}"` : ""}><h2>${title}</h2>${body.map((text) => `<p>${text}</p>`).join("")}</section>`;
}

function wrapCanvasText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const words = text.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let current = "";
  for (const word of words) {
    const test = current ? `${current} ${word}` : word;
    if (ctx.measureText(test).width > maxWidth && current) {
      lines.push(current);
      current = word;
    } else current = test;
  }
  if (current) lines.push(current);
  return lines;
}

function drawWrapped(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, maxWidth: number, lineHeight: number, maxLines: number): number {
  const wrapped = wrapCanvasText(ctx, text, maxWidth);
  const lines = wrapped.slice(0, maxLines);
  lines.forEach((line, index) => ctx.fillText(index === maxLines - 1 && wrapped.length > maxLines ? `${line.replace(/[,.!?;:]?$/, "")}…` : line, x, y + index * lineHeight));
  return y + lines.length * lineHeight;
}

async function createWordCardBlob(word: Word): Promise<Blob> {
  await document.fonts?.ready;
  const canvas = document.createElement("canvas");
  canvas.width = 1080;
  canvas.height = 1350;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas is not available");
  const dark = state.theme === "dark" || (state.theme === "system" && matchMedia("(prefers-color-scheme: dark)").matches);
  const background = dark ? "#181714" : "#f4f0e6";
  const ink = dark ? "#f0eadf" : "#25211d";
  const muted = dark ? "#b3aa9d" : "#71695f";
  const line = dark ? "#4a443d" : "#cfc5b6";
  const accent = lists.find((item) => item.id === word.listId)?.accent ?? (dark ? "#ca7c80" : "#7a343b");

  ctx.fillStyle = background;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.strokeStyle = line;
  ctx.lineWidth = 2;
  ctx.strokeRect(48, 48, 984, 1254);

  const hasRealEtymology = Boolean(local(word.etymology));
  const translation = wordTranslation(word);
  const termLength = wordTerm(word).length;
  const titleSize = termLength > 24 ? 74 : termLength > 14 ? 92 : 112;
  const titleLine = termLength > 24 ? 82 : 104;
  let y = 160;

  ctx.fillStyle = accent;
  ctx.fillRect(110, y - 12, 7, 54);
  ctx.fillStyle = muted;
  ctx.font = "bold 24px Geist, system-ui, sans-serif";
  ctx.fillText(local(word.partOfSpeech).toUpperCase(), 140, y + 14);

  ctx.fillStyle = ink;
  ctx.font = `500 ${titleSize}px "Cormorant Garamond", Georgia, serif`;
  y = drawWrapped(ctx, wordTerm(word), 110, 300, 860, titleLine, 3) + 18;
  if (translation) {
    ctx.fillStyle = accent;
    ctx.font = "italic 44px \"Cormorant Garamond\", Georgia, serif";
    y = drawWrapped(ctx, translation, 110, y, 820, 54, 2) + 52;
  } else {
    y += 28;
  }

  ctx.strokeStyle = line;
  ctx.beginPath();
  ctx.moveTo(110, y);
  ctx.lineTo(970, y);
  ctx.stroke();
  y += 66;

  ctx.fillStyle = ink;
  ctx.font = "42px \"Cormorant Garamond\", Georgia, serif";
  y = drawWrapped(ctx, local(word.definition), 110, y, 840, 58, 5) + 42;

  if (hasEditorialExample(word)) {
    ctx.fillStyle = muted;
    ctx.font = "italic 34px \"Cormorant Garamond\", Georgia, serif";
    y = drawWrapped(ctx, local(word.example), 145, y, 760, 48, 4) + 56;
  }

  if (hasRealEtymology) {
    ctx.fillStyle = muted;
    ctx.font = "26px Geist, system-ui, sans-serif";
    drawWrapped(ctx, local(word.etymology), 110, Math.min(y, 1060), 840, 38, 3);
  }

  ctx.fillStyle = accent;
  ctx.font = "bold 22px Geist, system-ui, sans-serif";
  ctx.fillText("ARS LINGUAE · WILLGUIMONT.COM", 110, 1215);
  return new Promise((resolve, reject) => canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error("Could not create image")), "image/png"));
}

async function shareWord(id: string, downloadOnly = false): Promise<void> {
  const word = words.find((item) => item.id === id);
  if (!word) return;
  const blob = await createWordCardBlob(word);
  const file = new File([blob], `${slug(wordTerm(word))}-ars-linguae.png`, { type: "image/png" });
  if (!downloadOnly && navigator.share && navigator.canShare?.({ files: [file] })) {
    try {
      await navigator.share({ title: wordTerm(word), text: `${wordTerm(word)} — ${local(word.definition)}\n${location.href}`, files: [file] });
      showToast(c("Mot partagé.", "Word shared."));
    } catch (error) {
      if (!(error instanceof DOMException && error.name === "AbortError")) throw error;
    }
    return;
  }
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = file.name;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  showToast(c("Image enregistrée.", "Image saved."));
}

function renderLists(): string {
  const availableLists = lists.filter((list) => words.filter((word) => wordInList(word, list.id)).length >= 5);
  const active = availableLists.filter((list) => state.enabledLists.includes(list.id));
  const inactive = availableLists.filter((list) => !state.enabledLists.includes(list.id)).sort((a, b) => local(a.title).localeCompare(local(b.title), state.locale));

  return `<div class="simple-page lists-page">
    ${pageHeader(c("Choisir ce que vous apprenez", "Choose what you learn"), c("Listes", "Lists"), `<span class="header-count">${active.length}</span>`)}
    <div class="lists-explainer"><p>${c("Les listes actives alimentent la découverte et les quiz. Activez seulement les thèmes qui vous donnent envie de revenir.", "Active lists supply discovery and quizzes. Enable only the themes that make you want to return.")}</p><strong>${enabledWords().length} ${c("entrées disponibles", "entries available")}</strong></div>
    <section class="list-group"><div class="list-group-title"><h2>${c("Listes actives", "Active lists")}</h2><span>${active.length}</span></div><div class="managed-lists">${active.map(renderManagedList).join("")}</div></section>
    ${inactive.length ? `<section class="list-group"><div class="list-group-title"><h2>${c("À découvrir", "More to discover")}</h2><span>${inactive.length}</span></div><div class="managed-lists">${inactive.map(renderManagedList).join("")}</div></section>` : ""}
  </div>`;
}

function renderManagedList(list: (typeof lists)[number]): string {
  const members = words.filter((word) => wordInList(word, list.id));
  const studied = members.filter((word) => state.progress[word.id]?.attempts).length;
  const enabled = state.enabledLists.includes(list.id);
  const language = list.language === "fr" ? "FR" : list.language === "en" ? "EN" : list.language === "la" ? "LA" : "FR · EN";
  return `<article class="managed-list ${enabled ? "enabled" : ""}" style="--word-accent:${list.accent}">
    <a class="managed-list-copy" href="#list/${encodeURIComponent(list.id)}"><div><span class="list-language">${language}</span><h3>${local(list.title)}</h3></div><p>${local(list.description)}</p><small>${members.length} ${t("words")}${studied ? ` · ${studied} ${c("étudiés", "studied")}` : ""}</small></a>
    <div class="managed-list-actions"><button class="list-toggle" data-toggle-list="${list.id}" role="switch" aria-checked="${enabled}" aria-label="${enabled ? c("Désactiver", "Disable") : c("Activer", "Enable")} ${local(list.title)}"><i></i><span>${enabled ? c("Active", "On") : c("Activer", "Enable")}</span></button>${icon("arrow")}</div>
  </article>`;
}

function renderListDetail(id: string): string {
  const list = lists.find((item) => item.id === id);
  if (!list) return renderLists();
  const members = words.filter((word) => wordInList(word, id)).sort((a, b) => wordTerm(a).localeCompare(wordTerm(b), state.locale));
  const enabled = state.enabledLists.includes(id);
  const studied = members.filter((word) => state.progress[word.id]?.attempts).length;
  const mastered = members.filter((word) => (state.progress[word.id]?.repetitions ?? 0) >= 3).length;
  const language = list.language === "fr" ? "Français" : list.language === "en" ? "English" : list.language === "la" ? "Latina" : "Français · English";
  return `<div class="simple-page list-detail-page" style="--word-accent:${list.accent}">
    <a class="back-to-lists" href="#lists">← ${c("Toutes les listes", "All lists")}</a>
    <header class="list-detail-header"><p class="eyebrow">${language}</p><div><h1>${local(list.title)}</h1><button class="list-toggle detail-toggle" data-toggle-list="${id}" role="switch" aria-checked="${enabled}"><i></i><span>${enabled ? c("Active", "On") : c("Activer", "Enable")}</span></button></div><p>${local(list.description)}</p></header>
    <div class="list-detail-stats"><span><strong>${members.length}</strong>${t("words")}</span><span><strong>${studied}</strong>${c("étudiés", "studied")}</span><span><strong>${mastered}</strong>${t("mastered")}</span></div>
    <div class="list-detail-actions"><button data-browse-list="${id}">${c("Voir dans Mes mots", "Open in My Words")} ${icon("arrow")}</button></div>
    <section class="detail-word-list"><div class="detail-word-list-title"><h2>${c("Tous les mots", "All words")}</h2><span>${members.length}</span></div><div class="simple-word-list">${members.map(renderWordRow).join("")}</div></section>
  </div>`;
}

function dueWords(): Word[] {
  return selectDueWords(enabledWords(), state.starred, state.progress);
}

function nextReviewDate(): number | null {
  return findNextReview(enabledWords(), state.starred, state.progress);
}

function reviewStats(): string {
  const progress = Object.values(state.progress);
  const attempts = progress.reduce((sum, item) => sum + item.attempts, 0);
  const correct = progress.reduce((sum, item) => sum + item.correct, 0);
  const mastered = progress.filter((item) => item.repetitions >= 3).length;
  const activity = new Set(state.activityDates);
  let streak = 0;
  const cursor = new Date();
  const key = (date: Date): string => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
  if (!activity.has(key(cursor))) cursor.setDate(cursor.getDate() - 1);
  while (activity.has(key(cursor))) { streak += 1; cursor.setDate(cursor.getDate() - 1); }
  return `<section class="review-stats"><span><strong>${mastered}</strong>${t("mastered")}</span><span><strong>${attempts ? Math.round((correct / attempts) * 100) : 0}%</strong>${t("accuracy")}</span><span><strong>${streak}</strong>${t("streak")}</span></section>`;
}

function renderReview(): string {
  if (session) return renderSession();
  const due = dueWords();
  const savedCount = enabledWords().filter((word) => state.starred.includes(word.id)).length;
  const nextDue = nextReviewDate();
  if (!savedCount) return `<div class="simple-page review-page">
    ${pageHeader(c("Retrouver pour retenir", "Recall to remember"), c("Réviser", "Review"))}
    <section class="review-empty reveal delay-1"><p class="eyebrow">${c("Votre première séance", "Your first session")}</p><h2>${c("Choisissez d’abord quelques mots.", "Choose a few words first.")}</h2><p>${c("Dans Découvrir, retenez les mots que vous souhaitez vraiment revoir. Ils reviendront ici au moment juste.", "In Discover, save the words you genuinely want to revisit. They will return here at the right time.")}</p><a class="primary-button" href="#home">${c("Découvrir des mots", "Discover words")} ${icon("arrow")}</a></section>${reviewStats()}
  </div>`;
  if (!due.length) return `<div class="simple-page review-page">
    ${pageHeader(c("Retrouver pour retenir", "Recall to remember"), c("Réviser", "Review"))}
    <section class="review-empty reveal delay-1"><p class="eyebrow">${c("Rien à revoir", "Nothing due")}</p><h2>${c("Vous êtes à jour.", "You are up to date.")}</h2><p>${nextDue ? c(`Prochaine révision ${new Intl.DateTimeFormat("fr", { dateStyle: "long" }).format(nextDue)}.`, `Next review ${new Intl.DateTimeFormat("en", { dateStyle: "long" }).format(nextDue)}.`) : ""}</p><a class="primary-button" href="#home">${c("Continuer à découvrir", "Keep discovering")} ${icon("arrow")}</a></section>${reviewStats()}
  </div>`;
  return `<div class="simple-page review-page">
    ${pageHeader(c("Retrouver pour retenir", "Recall to remember"), c("Réviser", "Review"))}
    <section class="review-intro reveal delay-1">
      <div class="review-count"><strong>${due.length}</strong><span>${c("mots prêts", "words ready")}</span></div>
      <div><h2>${c("Une courte séance", "A short session")}</h2><p>${c("Définitions, contextes, synonymes et antonymes. Chaque question fait revenir le mot sous un angle différent.", "Definitions, context, synonyms and antonyms. Each question brings the word back from a different angle.")}</p></div>
      <button class="primary-button" data-start-session>${c("Commencer", "Begin")} ${icon("arrow")}</button>
    </section>
    ${reviewStats()}
    <section class="quiz-types reveal delay-2">
      <p class="eyebrow">${c("Dans cette séance", "In this session")}</p>
      <div>${[
        [c("Définition", "Definition"), c("Trouver le mot", "Find the word")],
        [c("Contexte", "Context"), c("Compléter la phrase", "Complete the sentence")],
        [c("Rappel", "Recall"), c("Écrire le mot sans choix", "Type the word without choices")],
        [c("Synonyme", "Synonym"), c("Reconnaître le sens proche", "Recognise a close meaning")],
        [c("Antonyme", "Antonym"), c("Distinguer le sens opposé", "Distinguish the opposite")],
      ].map(([title, text]) => `<article><strong>${title}</strong><span>${text}</span></article>`).join("")}</div>
    </section>
  </div>`;
}

function startSession(): void {
  const pool = shuffle(dueWords()).slice(0, 7);
  if (!pool.length) return;
  session = { words: pool, index: 0, correct: 0, answered: false, wasCorrect: false };
  if (route === "practice") render(); else location.hash = "practice";
}

function shuffle<T>(items: T[]): T[] {
  const result = [...items];
  for (let index = result.length - 1; index > 0; index--) {
    const random = Math.floor(Math.random() * (index + 1));
    [result[index], result[random]] = [result[random], result[index]];
  }
  return result;
}

function otherWords(current: Word): Word[] {
  const active = enabledWords().filter((word) => word.id !== current.id);
  const sameLanguage = active.filter((word) => word.language === current.language);
  return shuffle([...sameLanguage, ...active.filter((word) => !sameLanguage.includes(word))]).slice(0, 3);
}

function makeQuestion(word: Word, index: number): QuizQuestion {
  const availableKinds: QuestionKind[] = ["definition", "meaning", "spelling"];
  if (hasEditorialExample(word)) availableKinds.push("example");
  if (localList(word.synonyms).length) availableKinds.push("synonym");
  if (word.antonyms?.[state.locale].length) availableKinds.push("antonym");
  const kind = availableKinds[index % availableKinds.length];
  const others = otherWords(word);

  if (kind === "meaning") return {
    kind, prompt: c("Quelle définition convient à ce mot ?", "Which definition belongs to this word?"), stem: wordTerm(word), correctId: word.id,
    options: shuffle([word, ...others]).map((item) => ({ id: item.id, label: local(item.definition) })),
  };
  if (kind === "spelling") return {
    kind,
    prompt: c("Écrivez le mot qui correspond à cette définition.", "Type the word that matches this definition."),
    stem: local(word.definition),
    correctId: word.id,
    options: [],
  };
  if (kind === "synonym" || kind === "antonym") {
    const source = kind === "synonym" ? localList(word.synonyms) : localList(word.antonyms!);
    const correct = source[0];
    const distractors = (kind === "synonym"
      ? [localList(word.antonyms ?? word.synonyms)[0], ...others.map((item) => localList(item.synonyms)[0] ?? wordTerm(item))]
      : [localList(word.synonyms)[0], ...others.map((item) => localList(item.antonyms ?? item.synonyms)[0] ?? wordTerm(item))]).filter(Boolean);
    return {
      kind,
      prompt: kind === "synonym" ? c("Quel mot a le sens le plus proche ?", "Which word is closest in meaning?") : c("Quel mot exprime le contraire ?", "Which word expresses the opposite?"),
      stem: wordTerm(word), correctId: "correct",
      options: shuffle([{ id: "correct", label: correct }, ...distractors.filter((item) => item !== correct).slice(0, 3).map((label, i) => ({ id: `other-${i}`, label }))]),
    };
  }
  const escapedTerm = wordTerm(word).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const stem = kind === "example" ? `“${local(word.example).replace(new RegExp(escapedTerm, "gi"), "_______")}”` : local(word.definition);
  return {
    kind, prompt: kind === "example" ? c("Quel mot complète cette phrase ?", "Which word completes this sentence?") : c("Quel mot correspond à cette définition ?", "Which word matches this definition?"),
    stem, correctId: word.id,
    options: shuffle([word, ...others]).map((item) => ({ id: item.id, label: wordTerm(item) })),
  };
}

function renderSession(): string {
  if (!session) return "";
  if (session.index >= session.words.length) return renderComplete();
  const word = session.words[session.index];
  session.question ??= makeQuestion(word, session.index);
  const question = session.question;
  return `<div class="quiz-page">
    <header class="quiz-header"><button data-end-session aria-label="${t("close")}">${icon("close")}</button><div><span>${session.index + 1} / ${session.words.length}</span><i role="progressbar" aria-valuemin="0" aria-valuemax="${session.words.length}" aria-valuenow="${session.index + (session.answered ? 1 : 0)}"><b style="width:${((session.index + (session.answered ? 1 : 0)) / session.words.length) * 100}%"></b></i></div></header>
    <section class="quiz-question"><p>${question.prompt}</p><h1>${question.stem}</h1></section>
    ${question.kind === "spelling" && !session.answered ? `<form class="quiz-spelling" data-spelling-form><label for="spelling-answer">${c("Votre réponse", "Your answer")}</label><div><input id="spelling-answer" name="answer" autocomplete="off" autocapitalize="none" spellcheck="false"><button>${c("Vérifier", "Check")}</button></div></form>` : `<section class="quiz-options">${question.options.map((option) => {
      const selected = session?.selectedId === option.id;
      const correct = session?.answered && option.id === question.correctId;
      const wrong = session?.answered && selected && !correct;
      return `<button data-answer="${option.id}" class="${correct ? "correct" : ""} ${wrong ? "wrong" : ""}" ${session?.answered ? "disabled" : ""}><span>${option.label}</span>${correct ? icon("check") : ""}</button>`;
    }).join("")}</section>`}
    ${session.answered ? `<footer class="quiz-feedback" role="status" aria-live="polite"><div><strong>${session.wasCorrect ? c("Oui, exactement.", "Yes, exactly.") : c("Pas tout à fait.", "Not quite.")}</strong><p><b>${wordTerm(word)}</b> — ${local(word.definition)}</p></div><div class="rating-actions">${session.wasCorrect ? `<button data-rate="again">${c("Encore", "Again")}</button><button data-rate="hard">${c("Difficile", "Hard")}</button><button data-rate="good">${c("Bien", "Good")}</button><button data-rate="easy">${c("Facile", "Easy")}</button>` : `<button data-rate="again">${c("Revoir demain", "Review tomorrow")}</button>`}</div></footer>` : ""}
  </div>`;
}

function answerQuestion(id: string): void {
  if (!session || session.answered || !session.question) return;
  const word = session.words[session.index];
  const correct = id === session.question.correctId;
  session.answered = true;
  session.wasCorrect = correct;
  session.selectedId = id;
  if (correct) session.correct += 1;
  if (state.sounds) playTone(correct);
  render();
}

function answerSpelling(value: string): void {
  if (!session || session.answered || session.question?.kind !== "spelling") return;
  const normalize = (text: string): string => text.normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim().toLocaleLowerCase(state.locale);
  const word = session.words[session.index];
  const accepted = [wordTerm(word), word.term, word.termEn, ...(word.aliases ?? [])].filter((item): item is string => Boolean(item)).map(normalize);
  const correct = accepted.includes(normalize(value));
  session.answered = true;
  session.wasCorrect = correct;
  session.selectedId = correct ? word.id : "typed-wrong";
  if (correct) session.correct += 1;
  if (state.sounds) playTone(correct);
  render();
}

function rateAnswer(rating: ReviewRating): void {
  if (!session?.answered) return;
  const word = session.words[session.index];
  state.progress[word.id] = schedule(state.progress[word.id], rating);
  if (!state.activityDates.includes(todayKey())) state.activityDates.push(todayKey());
  persist();
  session.index += 1;
  session.answered = false;
  session.selectedId = undefined;
  session.question = undefined;
  render();
}

function playTone(correct: boolean): void {
  try {
    const context = new AudioContext();
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.frequency.setValueAtTime(correct ? 520 : 210, context.currentTime);
    oscillator.frequency.linearRampToValueAtTime(correct ? 650 : 175, context.currentTime + .13);
    gain.gain.setValueAtTime(.035, context.currentTime);
    gain.gain.exponentialRampToValueAtTime(.001, context.currentTime + .18);
    oscillator.connect(gain).connect(context.destination);
    oscillator.start(); oscillator.stop(context.currentTime + .18);
  } catch { /* Sound is optional. */ }
}

function renderComplete(): string {
  if (!session) return "";
  return `<div class="quiz-complete"><span>${session.correct}/${session.words.length}</span><p class="eyebrow">${c("Séance terminée", "Session complete")}</p><h1>${c("Les mots reviennent.", "The words return.")}</h1><p>${c("Revenez demain : quelques minutes suffisent pour les garder vivants.", "Return tomorrow: a few minutes are enough to keep them alive.")}</p><button class="primary-button" data-complete-session>${c("Découvrir un autre mot", "Discover another word")} ${icon("arrow")}</button></div>`;
}

function renderSettings(): string {
  return `<div class="simple-page settings-page">
    ${pageHeader("Ars Linguae", t("settings"))}
    <section class="plain-setting reveal delay-1"><p class="eyebrow">${t("language")}</p><div class="simple-choice"><button class="${state.locale === "fr" ? "active" : ""}" data-locale="fr">Français</button><button class="${state.locale === "en" ? "active" : ""}" data-locale="en">English</button></div></section>
    <section class="plain-setting reveal delay-1"><p class="eyebrow">${c("Mots étudiés", "Words studied")}</p><p class="setting-help">${c("Ce choix est indépendant de la langue de l’interface.", "This is independent from the interface language.")}</p><div class="simple-choice study-language-choice">${(["all", "fr", "en", "la"] as StudyLanguage[]).map((language) => `<button class="${state.studyLanguage === language ? "active" : ""}" data-study-language="${language}">${language === "all" ? c("Tous", "All") : language.toUpperCase()}</button>`).join("")}</div></section>
    <section class="plain-setting reveal delay-1"><p class="eyebrow">${t("appearance")}</p><div class="simple-choice">${(["light", "dark", "system"] as Theme[]).map((theme) => `<button class="${state.theme === theme ? "active" : ""}" data-theme="${theme}">${t(theme)}</button>`).join("")}</div></section>
    <section class="plain-setting reveal delay-2"><label class="plain-toggle"><span><strong>${t("sound")}</strong><small>${t("soundDesc")}</small></span><input id="sound-toggle" type="checkbox" ${state.sounds ? "checked" : ""}></label></section>
    <section class="plain-setting"><p class="eyebrow">${c("Application", "App")}</p>${deferredInstall ? `<button class="plain-action" data-install>${t("install")} ${icon("arrow")}</button>` : `<p class="install-help">${c("Pour l’installer, ouvrez le menu de partage ou le menu du navigateur, puis choisissez « Ajouter à l’écran d’accueil » ou « Installer l’application ».", "To install it, open the browser or share menu, then choose “Add to Home Screen” or “Install app”.")}</p>`}</section>
    <section class="plain-setting reveal delay-2"><p class="eyebrow">${t("data")}</p><div class="plain-actions"><button data-export>${t("export")}${icon("download")}</button><label>${t("import")}${icon("upload")}<input id="import-file" type="file" accept="application/json"></label><button class="danger" data-reset>${t("reset")}</button></div></section>
  </div>`;
}

app.addEventListener("click", (event) => {
  const target = event.target as HTMLElement;
  const control = target.closest<HTMLElement>("[data-star],[data-share-word],[data-next-discovery],[data-open-list],[data-filter],[data-word],[data-view-list],[data-toggle-list],[data-browse-list],[data-scroll-section],[data-start-session],[data-answer],[data-rate],[data-next-question],[data-end-session],[data-complete-session],[data-locale],[data-study-language],[data-theme],[data-export],[data-reset],[data-install]");
  if (!control) return;
  if (control.dataset.star) {
    event.stopPropagation();
    const id = control.dataset.star;
    state.starred = state.starred.includes(id) ? state.starred.filter((item) => item !== id) : [...state.starred, id];
    if (!state.seenWords.includes(id)) state.seenWords.push(id);
    persist(); render(); showToast(state.starred.includes(id) ? c("Ajouté aux mots à retenir.", "Added to words to remember.") : c("Retiré des mots à retenir.", "Removed from words to remember.")); return;
  }
  if (control.dataset.shareWord) { shareWord(control.dataset.shareWord).catch(() => alert(c("Impossible de créer l’image de partage.", "Could not create the share image."))); return; }
  if (control.hasAttribute("data-next-discovery")) {
    const active = enabledWords();
    const unseen = active.filter((word) => !state.seenWords.includes(word.id));
    const pool = unseen.length ? unseen : active;
    const current = pool[discoveryIndex % pool.length];
    if (current && !state.seenWords.includes(current.id)) state.seenWords.push(current.id);
    if (active.length && active.every((word) => state.seenWords.includes(word.id))) state.seenWords = [];
    discoveryIndex %= Math.max(1, active.filter((word) => !state.seenWords.includes(word.id)).length);
    persist(); render(); return;
  }
  if (control.dataset.openList) { location.hash = `list/${encodeURIComponent(control.dataset.openList)}`; return; }
  if (control.dataset.filter) { libraryFilter = control.dataset.filter as "all" | "starred"; render(); return; }
  if (control.dataset.toggleList) {
    event.stopPropagation();
    const id = control.dataset.toggleList;
    if (state.enabledLists.includes(id)) {
      if (state.enabledLists.length === 1) return;
      state.enabledLists = state.enabledLists.filter((item) => item !== id);
      if (selectedList === id) selectedList = "all";
    } else state.enabledLists = [...state.enabledLists, id];
    discoveryIndex = 0; persist(); render(); showToast(state.enabledLists.includes(id) ? c("Liste activée.", "List enabled.") : c("Liste désactivée.", "List disabled.")); return;
  }
  if (control.dataset.viewList) { location.hash = `list/${encodeURIComponent(control.dataset.viewList)}`; return; }
  if (control.dataset.browseList) { selectedList = control.dataset.browseList; location.hash = "library"; return; }
  if (control.dataset.scrollSection) { document.getElementById(control.dataset.scrollSection)?.scrollIntoView({ behavior: "smooth", block: "start" }); return; }
  if (control.dataset.word) { location.hash = `word/${encodeURIComponent(control.dataset.word)}`; return; }
  if (control.hasAttribute("data-start-session")) { startSession(); return; }
  if (control.dataset.answer) { answerQuestion(control.dataset.answer); return; }
  if (control.dataset.rate) { rateAnswer(control.dataset.rate as ReviewRating); return; }
  if (control.hasAttribute("data-next-question") && session) { session.index += 1; session.answered = false; session.selectedId = undefined; session.question = undefined; render(); return; }
  if (control.hasAttribute("data-end-session")) { session = null; render(); return; }
  if (control.hasAttribute("data-complete-session")) { session = null; location.hash = "home"; return; }
  if (control.dataset.locale) { state.locale = control.dataset.locale as Locale; persist(); app.innerHTML = ""; render(); return; }
  if (control.dataset.studyLanguage) { state.studyLanguage = control.dataset.studyLanguage as StudyLanguage; discoveryIndex = 0; persist(); render(); return; }
  if (control.dataset.theme) { state.theme = control.dataset.theme as Theme; persist(); render(); return; }
  if (control.hasAttribute("data-export")) { exportState(state); return; }
  if (control.hasAttribute("data-reset") && confirm(t("resetConfirm"))) { state.progress = {}; state.starred = []; state.activityDates = []; state.seenWords = []; persist(); render(); return; }
  if (control.hasAttribute("data-install") && deferredInstall) { deferredInstall.prompt(); deferredInstall = null; render(); }
});

app.addEventListener("submit", (event) => {
  const form = (event.target as HTMLElement).closest<HTMLFormElement>("[data-spelling-form]");
  if (!form) return;
  event.preventDefault();
  answerSpelling(new FormData(form).get("answer")?.toString() ?? "");
});

app.addEventListener("input", (event) => {
  const target = event.target as HTMLInputElement;
  if (target.id === "word-search") {
    query = target.value;
    const position = target.selectionStart;
    clearTimeout(searchRenderTimer);
    searchRenderTimer = window.setTimeout(() => {
      render();
      const input = document.querySelector<HTMLInputElement>("#word-search");
      input?.focus(); if (position !== null) input?.setSelectionRange(position, position);
    }, 120);
  }
  if (target.id === "sound-toggle") { state.sounds = target.checked; persist(); }
});

app.addEventListener("change", async (event) => {
  const target = event.target as HTMLInputElement | HTMLSelectElement;
  if (target.id === "list-select") { selectedList = target.value; render(); }
  if (target.id === "import-file" && target instanceof HTMLInputElement && target.files?.[0]) {
    try { state = await importState(target.files[0]); validateListSelection(state); persist(); app.innerHTML = ""; render(); showToast(c("Sauvegarde restaurée.", "Backup restored.")); } catch { alert(c("Cette sauvegarde est illisible.", "This backup could not be read.")); }
  }
});

window.addEventListener("hashchange", () => {
  if (route !== "word") wordReturnHash = route === "lists" && listDetailId ? `#list/${encodeURIComponent(listDetailId)}` : `#${route}`;
  route = routeFromHash(); listDetailId = listIdFromHash(); wordDetailId = wordIdFromHash();
  if (route !== "practice") session = null;
  animateNextRender = true; render(); scrollTo({ top: 0 });
});
window.addEventListener("beforeinstallprompt", (event) => { event.preventDefault(); deferredInstall = event as BeforeInstallPromptEvent; render(); });
matchMedia("(prefers-color-scheme: dark)").addEventListener("change", applyTheme);

applyTheme();
render();

if ("serviceWorker" in navigator && import.meta.env.PROD) window.addEventListener("load", () => {
  const controlledAtLoad = Boolean(navigator.serviceWorker.controller);
  navigator.serviceWorker.register(`${import.meta.env.BASE_URL}sw.js`).then((registration) => {
    registration.addEventListener("updatefound", () => {
      const worker = registration.installing;
      worker?.addEventListener("statechange", () => {
        if (worker.state === "installed" && navigator.serviceWorker.controller && confirm(c("Une nouvelle version est prête. La charger maintenant ?", "A new version is ready. Load it now?"))) worker.postMessage("SKIP_WAITING");
      });
    });
  }).catch(() => { /* The app remains usable without installation. */ });
  let reloading = false;
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (controlledAtLoad && !reloading) { reloading = true; location.reload(); }
  });
});
